/**
 * The producer's script for THE FIRST BROADCAST. One lesson at a time; each
 * either waits for a console action (`done`) or an ack (Enter / CONTINUE).
 *
 * Timing comes from the precomputed timeline (tl), so it adapts to the seed:
 * lessons trigger a beat before the moment they teach.
 */
import type { DirectorState, GraphicKind, MatchEventType, WorldSnapshot } from '../core/contracts';
import type { MatchSession } from '../game/session';
import type { DimLevel, Spot } from './producer';
import type { TrainingTimeline } from './timeline';

export interface LessonCtx {
  tl: TrainingTimeline;
  readonly s: Readonly<WorldSnapshot>;
  readonly d: Readonly<DirectorState>;
  /** Real seconds in the current lesson. */
  readonly elapsed: number;
  flags: Set<string>;
  vars: Record<string, string>;
  session: MatchSession;
  spot: {
    pgm(label?: string): Spot;
    tile(n: number | string, label?: string): Spot;
    cam(camId: string, key?: string): Spot;
    wall(): Spot;
    key(k: string, label?: string): Spot;
    sel(sel: string, label?: string): Spot;
  };
  onAir(kind: GraphicKind): boolean;
  program(): string;
}

export interface Lesson {
  id: string;
  kicker?: string;
  text: string | ((c: LessonCtx) => string);
  /** Rewrite the line mid-lesson when this returns a new string. */
  retext?: (c: LessonCtx) => string | null;
  spots?: (c: LessonCtx) => Spot[];
  /** Start condition (polled). Omitted = immediately after the previous lesson. */
  when?: (c: LessonCtx) => boolean;
  /** Skip silently at start (goal already met / no longer possible). */
  skipIf?: (c: LessonCtx) => boolean;
  /** Completion (the player did it). */
  done?: (c: LessonCtx) => boolean;
  /** Move on without credit (moment passed / info line timed out). */
  giveUp?: (c: LessonCtx) => boolean;
  /** Needs an explicit CONTINUE (Enter). Implies a hold. */
  ack?: string;
  /** Sim speed while the lesson is up. */
  scale?: number;
  dim?: DimLevel;
  /** Freeze the sim (scale 0, full dim) after this many real seconds without completion. */
  holdAfter?: number;
  holdIf?: (c: LessonCtx) => boolean;
  minShow?: number;
  praise?: string | ((c: LessonCtx) => string);
  chime?: boolean;
  /** Not counted in the lesson numbering (info beats). */
  unnumbered?: boolean;
  /** Ends the training on completion. */
  final?: boolean;
  dock?: 'center' | 'pgm';
  onEnter?(c: LessonCtx): void;
  onDone?(c: LessonCtx): void;
}

const DEAD_PHASES = new Set(['stoppage', 'set_piece', 'goal_celebration']);
const LOWER_THIRDS: GraphicKind[] = ['player_lower_third', 'goal_lower_third', 'card_lower_third', 'sub_lower_third'];
const CHANCE_LINES: Partial<Record<MatchEventType, string>> = {
  woodwork: 'Off the woodwork!', save: 'What a save!', near_miss: 'Inches wide!', goal: 'GOAL!',
};

const cam = (n: string) => `CAM ${n}`;
const replayOn = (c: LessonCtx) => c.d.replay.active;

/** Pick a replay angle that differs from what's rolling: behind the goal at the action's end first. */
function pickAngle(c: LessonCtx, x: number): string {
  const prefs = x < 0 ? ['5', '3', '6', '4'] : ['6', '4', '5', '3'];
  return prefs.find((n) => cam(n) !== c.d.replay.camId) ?? '5';
}

/** What happened to the first shot (for the producer's reaction). */
function firstChanceLine(c: LessonCtx): string {
  const evs = c.session.events();
  for (const e of evs) {
    if (e.t < c.tl.shotT - 0.1 || e.t > c.tl.shotT + 3) continue;
    const line = CHANCE_LINES[e.type];
    if (line) return line;
  }
  return 'Chance gone.';
}

export const LESSONS: Lesson[] = [
  {
    id: 'welcome',
    kicker: 'THE FIRST BROADCAST',
    text: "Welcome to the truck. I'm your *producer* — I'll be in your ear tonight. The camera operators are AI; *you* choose what goes to air. One thing at a time, and I'll hold the match whenever I need you.",
    ack: "LET'S GO",
    scale: 0, dim: 2,
    onEnter: (c) => { if (c.program() !== 'CAM 2') c.session.dispatch({ type: 'hot_punch', camId: 'CAM 2' }); },
  },
  {
    id: 'program',
    kicker: 'CUTTING',
    text: 'This is *PROGRAM* — what the world sees. Keep the ball on it. Press [1] for MAIN WIDE.',
    spots: (c) => [c.spot.pgm('PROGRAM · ON AIR'), c.spot.tile(1, 'MAIN WIDE')],
    done: (c) => c.program() === 'CAM 1',
    scale: 0, holdAfter: 0,
    praise: "That's the shot. Wide keeps the whole move in view — it's your home base.",
  },
  {
    id: 'wall',
    kicker: 'CUTTING',
    text: 'Your *monitor wall* — every camera, live. A number key punches that camera straight to air. Tally lights: *red* is on air, *green* is preview.',
    spots: (c) => [c.spot.wall()],
    ack: 'GOT IT',
    scale: 0, dim: 2,
  },
  {
    id: 'kickoff',
    unnumbered: true,
    text: "We're live. Hold the wide and let it breathe. Keep an eye on *INTENSITY* — when it climbs, an attack is on. I'll shout.",
    spots: (c) => [c.spot.sel('.sd-intensity', 'INTENSITY')],
    giveUp: (c) => c.elapsed > 6.5 || c.s.t > c.tl.shotT - 3,
    scale: 1,
  },
  {
    id: 'tight',
    kicker: 'SHOT SIZES',
    when: (c) => c.s.t >= c.tl.shotT - 1.4,
    skipIf: (c) => c.program() === 'CAM 2',
    text: 'Shot coming — go *tight*! Press [2].',
    spots: (c) => [c.spot.tile(2, 'MAIN TIGHT')],
    done: (c) => c.program() === 'CAM 2',
    scale: 0.25, holdAfter: 1.8, minShow: 0.3,
    praise: 'Tight on the striker…',
    chime: false,
  },
  {
    id: 'wide',
    kicker: 'SHOT SIZES',
    when: (c) => c.s.t >= c.tl.stopT || DEAD_PHASES.has(c.s.phase),
    skipIf: (c) => c.program() === 'CAM 1',
    text: (c) => `${firstChanceLine(c)} Back to wide — [1].`,
    spots: (c) => [c.spot.tile(1, 'MAIN WIDE')],
    done: (c) => c.program() === 'CAM 1',
    scale: 0.25, holdAfter: 1.5,
    praise: 'Wide for the build-up, tight for the strike, wide again. That rhythm is the job.',
  },
  {
    id: 'replay',
    kicker: 'REPLAYS',
    when: (c) => DEAD_PHASES.has(c.s.phase) || c.s.t >= c.tl.stopT,
    text: "Ball's out. Perfect time for a *replay* — press [R].",
    spots: (c) => [c.spot.key('R', 'REPLAY')],
    done: replayOn,
    giveUp: (c) => c.s.phase === 'open_play' && c.s.intensity > 0.5,
    scale: 0.2, holdAfter: 2,
  },
  {
    id: 'angle',
    kicker: 'REPLAYS',
    skipIf: (c) => !replayOn(c),
    onEnter: (c) => { c.vars.angle = pickAngle(c, c.tl.shotX); },
    text: (c) => `Rolling. Try another angle — press [${c.vars.angle}].`,
    spots: (c) => [c.spot.tile(c.vars.angle!), c.spot.sel('.sd-rp', 'EVS')],
    done: (c) => !replayOn(c) || c.d.replay.camId === cam(c.vars.angle!),
    scale: 0.2, minShow: 0.4,
  },
  {
    id: 'live',
    kicker: 'REPLAYS',
    skipIf: (c) => !replayOn(c),
    text: 'Lovely. Back to live — [R].',
    spots: (c) => [c.spot.key('R', 'BACK TO LIVE')],
    done: (c) => !replayOn(c),
    scale: 0.2,
    praise: 'Textbook. Replays go at stoppages — never over live play.',
  },
  {
    id: 'scorebug',
    kicker: 'GRAPHICS',
    when: (c) => !replayOn(c) && c.s.t >= c.tl.calmT && c.s.phase === 'open_play' && c.s.intensity < 0.4,
    onEnter: (c) => { if (c.onAir('scorebug')) c.session.dispatch({ type: 'scorebug_toggle' }); },
    text: "Uh-oh — the *scorebug*'s dropped off air. It's our friend: viewers need the score and clock. Press [S] to bring it back.",
    spots: (c) => [c.spot.key('S', 'SCOREBUG')],
    done: (c) => c.onAir('scorebug'),
    scale: 0.3, holdAfter: 1.2,
    praise: 'Back up. The scorebug stays on all match.',
  },
  {
    id: 'lower3',
    kicker: 'GRAPHICS',
    text: '[Q] puts up a *lower third* — the name of the player on the ball. Try it.',
    spots: (c) => [c.spot.key('Q', 'LOWER 3RD')],
    done: (c) => LOWER_THIRDS.some((k) => c.onAir(k)),
    scale: 0.3, holdAfter: 2,
    praise: 'Name, number, done. Lower thirds clear themselves after a few seconds.',
  },
  {
    id: 'booth',
    kicker: 'THE BOOTH',
    onEnter: (c) => {
      const r = c.session.injectRequest("How are we doing on possession? Let's get the numbers up.", { graphic: 'possession_bar' }, 14);
      if (r) c.vars.req = String(r.id);
    },
    text: 'The commentators want something — read the *BOOTH* panel and give it to them.',
    spots: (c) => [c.spot.sel('.sd-booth .sd-card__head, .sd-booth .sd-req', 'TALKBACK · BOOTH')],
    done: (c) => c.onAir('possession_bar'),
    scale: 0.3, holdAfter: 3, minShow: 0.8,
    praise: 'They love you. Every booth request shows the key it needs — answer before the bar runs out.',
  },
  {
    id: 'wait',
    unnumbered: true,
    text: (c) => (c.program() === 'CAM 1'
      ? "Now we wait. Hold the wide. Big moments *build* — when INTENSITY spikes, stay with the ball."
      : 'Get back to wide — [1] — and hold it. Big moments *build*: when INTENSITY spikes, stay with the ball.'),
    spots: (c) => [c.spot.sel('.sd-intensity', 'INTENSITY')],
    giveUp: (c) => c.elapsed > 6 || c.s.t >= c.tl.buildT - 0.4,
    scale: 1,
  },
  {
    id: 'build',
    kicker: 'NEVER MISS A GOAL',
    when: (c) => c.s.t >= c.tl.buildT,
    text: 'Here they come — big chance building! Stay *WIDE* on [1]. Never, ever miss a goal.',
    retext: (c) => {
      const ev = c.session.events().find((e) => e.t >= c.tl.buildT && e.type in CHANCE_LINES && e.type !== 'goal');
      return ev ? `${CHANCE_LINES[ev.type]} It's still alive — stay *WIDE* on [1]!` : null;
    },
    spots: (c) => [c.spot.pgm('ON AIR'), c.spot.tile(1, 'MAIN WIDE')],
    done: (c) => c.flags.has('ev:goal'),
    holdIf: (c) => c.program() !== 'CAM 1',
    scale: 0.5, holdAfter: 1.6, dim: 0,
    chime: false,
  },
  {
    id: 'goal',
    kicker: 'EMOTION',
    text: 'GOAL! Stay *tight* on the celebration — [7] or [8] for the handhelds.',
    spots: (c) => [c.spot.tile(7, 'HANDHELD L'), c.spot.tile(8, 'HANDHELD R')],
    done: (c) => c.program() === 'CAM 7' || c.program() === 'CAM 8',
    scale: 0.25, holdAfter: 2.2, minShow: 0.4,
    praise: "That's the picture of the night. Emotion = tight.",
  },
  {
    id: 'goalreplay',
    kicker: 'REPLAYS',
    when: (c) => c.s.t >= c.tl.goalT + 4,
    text: 'Replay it while they celebrate — *two angles* if you can. Roll it: [R].',
    spots: (c) => [c.spot.key('R', 'REPLAY')],
    done: replayOn,
    scale: 0.35, holdAfter: 2.5,
  },
  {
    id: 'goalangle',
    kicker: 'REPLAYS',
    skipIf: (c) => !replayOn(c),
    onEnter: (c) => { c.vars.angle2 = pickAngle(c, c.tl.goalX); },
    text: (c) => `Second angle — press [${c.vars.angle2}].`,
    spots: (c) => [c.spot.tile(c.vars.angle2!), c.spot.sel('.sd-rp', 'EVS')],
    done: (c) => !replayOn(c) || c.d.replay.camId === cam(c.vars.angle2!),
    scale: 0.35, minShow: 0.4,
  },
  {
    id: 'goallive',
    kicker: 'REPLAYS',
    skipIf: (c) => !replayOn(c),
    text: 'Gorgeous. Back to live — [R].',
    spots: (c) => [c.spot.key('R', 'BACK TO LIVE')],
    done: (c) => !replayOn(c),
    scale: 0.35,
    praise: 'Two angles on the goal. The gallery is applauding.',
  },
  {
    id: 'scorer',
    kicker: 'GRAPHICS',
    skipIf: (c) => c.d.graphicsLog.some((g) => g.kind === 'goal_lower_third') || c.d.graphics.some((g) => g.kind === 'goal_lower_third'),
    text: 'Last one: after a goal, [Q] names the *scorer*. Put it up.',
    spots: (c) => [c.spot.key('Q', 'SCORER')],
    done: (c) => c.onAir('goal_lower_third'),
    scale: 0.35, holdAfter: 2,
    praise: 'Scorer named. Viewers at home know exactly who did it.',
  },
  {
    id: 'wrap',
    kicker: "THAT'S A WRAP",
    text: "That's the job. Every match is graded — *coverage*, *shot choice*, *pacing*, replays, graphics, the booth. Go make some TV.",
    spots: (c) => [c.spot.sel('.sd-rating, .sd-cats', 'YOUR GRADE · LIVE')],
    ack: 'SEE YOUR REPORT',
    scale: 0, dim: 2,
    final: true,
  },
];
